import React, { useMemo } from 'react'
import { useStoreConfig } from 'config'
import {
    complexTextValueToString,
    formatPrice,
    getStringAttributeValue,
    getProductImages,
    getTrimmedUrl
} from '../util'
import { getFirstNotEmpty } from '@corratech/pylot-utils'
import { ProductInterface } from '@pylot-data/pylotschema'
import { ProductJsonLd } from './ProductJsonLd'
import { CommonMetaTags } from '../CommonMetaTags'
import Head from 'next/head'

export type ProductProps = {
    product: ProductInterface
    canonicalUrl: string
    titleOverride?: string
    descriptionOverride?: string
}

export const Product = ({
    product,
    canonicalUrl,
    titleOverride,
    descriptionOverride
}: ProductProps): JSX.Element => {
    const config = useStoreConfig()
    const imageURL = getProductImages(product, config)[0].url
    const { seo } = config

    const imageContent: HTMLImageElement | null = useMemo(
        () =>
            (typeof document !== 'undefined' &&
                document.querySelector(
                    `img[src="${product?.small_image?.url}"]`
                )) ||
            null,
        [product?.small_image?.url]
    )
    // In some cases, product name and description are provided via non-standard attributes
    const title = getFirstNotEmpty([
        titleOverride,
        product?.meta_title,
        getStringAttributeValue(product, 'name', config),
        seo.default.title
    ])

    const description = getFirstNotEmpty([
        descriptionOverride,
        product?.meta_description,
        complexTextValueToString(
            getStringAttributeValue(product, 'description', config),
            seo.product?.description?.regExp,
            seo.product?.description?.maxLength
        ),
        seo.default.description
    ])

    const price = formatPrice(
        getStringAttributeValue(product, 'price_amount', config),
        config
    )
    const priceCurrency = getStringAttributeValue(
        product,
        'price_currency',
        config
    )

    const generalData = {
        title: title,
        keyword: String(product.meta_keyword),
        description: String(description),
        canonicalUrl
    }

    const twitterData = {
        title: title,
        description: description,
        domain: seo.default.twitter?.handle,
        url: canonicalUrl,
        card: seo.default.twitter?.cardType,
        image: getTrimmedUrl(imageURL),
        site: seo.default.twitter?.site,
        creator: seo.default.twitter?.creator
    }

    const ogData = {
        title: title,
        description: description,
        url: canonicalUrl,
        siteName: seo.default.openGraph?.site_name,
        image: getTrimmedUrl(imageContent?.src || ''),
        imageAlt: imageContent?.alt,
        imageWidth: imageContent?.width,
        imageHeight: imageContent?.height
    }

    return (
        <>
            <CommonMetaTags
                generalData={generalData}
                twitterData={twitterData}
                ogData={ogData}
            />
            <Head>
                {/* PRODUCT META */}
                <meta name="product:price:amount" content={price} />
                <meta name="product:price:currency" content={priceCurrency} />
            </Head>
            <ProductJsonLd
                product={product}
                title={title}
                description={description}
            />
        </>
    )
}
